// src/pages/UserManagement.jsx
import { useState, useEffect, createContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, mapRow, mapRows } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { useDarkMode, DM } from "../context/DarkModeContext";
import DashboardHeader from "../components/DashboardHeader";


export default function UserManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, currentUser, createFacultyAccount, createBulkFacultyAccounts } = useAuth();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const d = darkMode;

  const preloadedUsers = location.state?.preloadedUsers;

  const [userList, setUserList] = useState(preloadedUsers || []);
  const [loading, setLoading] = useState(!preloadedUsers);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("faculty");
  const [showDeptAccounts, setShowDeptAccounts] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [toast, setToast] = useState("");
  const [adminProfile, setAdminProfile] = useState(null);
  const [selectedRightsUser, setSelectedRightsUser] = useState(null);
  const [showPass, setShowPass] = useState({}); // UID -> boolean
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [credentialsModalUsers, setCredentialsModalUsers] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    if (!preloadedUsers) setLoading(true);
    try {
      const [adminRes, usersRes] = await Promise.all([
        api.get(`/faculty/${currentUser.uid}/profile`),
        api.get("/users?orderBy=name"),
      ]);

      if (adminRes) setAdminProfile(mapRow(adminRes));
      setUserList(mapRows(usersRes));
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 4000); }
  async function handleLogout() { await logout(); navigate("/admin"); }

  async function handlePasswordReset(email) {
    try {
      await api.post("/auth/forgot-password", { email });
      showToast("✅ Password reset email sent to " + email);
    } catch { showToast("❌ Failed to send reset email"); }
  }

  async function handleToggleStatus(user) {
    if (user.id === currentUser?.uid) return showToast("❌ Cannot change your own status!");
    const isActive = user.status !== "inactive";
    if (!window.confirm(`${isActive ? "Deactivate" : "Activate"} "${user.name}"?`)) return;
    try {
      await api.put(`/users/${user.id}`, { status: isActive ? "inactive" : "active" });
      showToast(`✅ "${user.name}" is now ${isActive ? "Inactive" : "Active"}`);
      fetchAll();
    } catch { showToast("❌ Failed"); }
  }

  async function handleMakeAdmin(user) {
    if (!window.confirm(`Give ADMIN rights to "${user.name}"?`)) return;
    try {
      await api.put(`/users/${user.id}`, { role: "admin" });
      showToast(`✅ "${user.name}" is now an Admin`);
      fetchAll();
    } catch { showToast("❌ Failed"); }
  }

  async function handleDeleteUser(user) {
    if (user.id === currentUser?.uid) return showToast("❌ Cannot delete yourself!");
    if (!window.confirm(`⚠️ PERMANENTLY DELETE "${user.name}"?\nThis will remove their profile and data from Firestore. (Note: Auth account remains)`)) return;
    try {
      await api.delete(`/users/${user.id}`);
      showToast(`✅ "${user.name}" has been deleted`);
      fetchAll();
    } catch { showToast("❌ Failed to delete user"); }
  }

  async function handleRemoveAdmin(user) {

    if (user.id === currentUser?.uid) return showToast("❌ Cannot remove your own admin rights!");
    if (!window.confirm(`Remove ADMIN rights from "${user.name}"?`)) return;
    try {
      await api.put(`/users/${user.id}`, { role: "faculty" });
      showToast(`✅ "${user.name}" is now a Faculty member`);
      fetchAll();
    } catch { showToast("❌ Failed"); }
  }

  const filtered = userList.filter(u => {
    // If showDeptAccounts is false, hide accounts that look like department common IDs
    // Department common accounts: faculty role, no empId
    if (!showDeptAccounts && u.role === "faculty" && !u.empId?.trim()) return false;

    const deptStr = (() => { if (u.designationHistory?.length) { const m = u.designationHistory.find(d => d.isMain) || u.designationHistory[0]; if (m?.dept) return m.dept; } return u.department || ""; })();
    const matchSearch = u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.empId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deptStr.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === "all" ? true : statusFilter === "inactive" ? u.status === "inactive" : u.status !== "inactive";
    const matchRole = roleFilter === "all" ? true : u.role === roleFilter;
    return matchSearch && matchStatus && matchRole;
  });

  const deptAccountCount = userList.filter(u => u.role === "faculty" && !u.empId?.trim()).length;
  const realFacultyCount = userList.filter(u => u.role === "faculty" && u.empId?.trim()).length;

  const roleColor = { superadmin: "#d97706", admin: "#7c3aed", faculty: "#2563eb" };
  const roleBg = { superadmin: "#fef3c7", admin: "#f5f3ff", faculty: "#eaf1ff" };
  const roleLabel = { superadmin: "⭐ Super Admin", admin: "👑 Admin", faculty: "🎓 Faculty" };

  const getNameWithTitle = (u) => {
    const title = u.title || u.salutation || u.prefix || "";
    if (!title) return u.name || "—";
    return `${title} ${u.name}`.trim();
  };

  // Reads department from designationHistory (isMain entry) — same source as Show Faculty Details
  const getDept = (u) => {
    if (u.designationHistory?.length) {
      const main = u.designationHistory.find(d => d.isMain) || u.designationHistory[0];
      if (main?.dept) return main.dept;
    }
    return u.department || "";
  };

  const togglePass = (id) => setShowPass(prev => ({ ...prev, [id]: !prev[id] }));

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedUsers(filtered.map(u => u.id));
    else setSelectedUsers([]);
  };

  const handleSelectUser = (id) => {
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(uid => uid !== id) : [...prev, id]);
  };

  const openCredentialsModal = () => {
    if (selectedUsers.length === 0) return showToast("❌ Please select users first");
    const usersToNotify = userList.filter(u => selectedUsers.includes(u.id));
    setCredentialsModalUsers(usersToNotify);
  };

  return (
    <div style={{ minHeight: "100vh", background: DM.pageBg(d), fontFamily: "Georgia, serif" }}>

      <DashboardHeader label="User Management">
        <button onClick={() => navigate("/admin/dashboard")} style={{ background: "#fff", color: "#1d4ed8", border: "none", borderRadius: 10, padding: "8px 18px", fontWeight: 800, fontSize: 18, cursor: "pointer" }}>← Dashboard</button>
        <button onClick={toggleDarkMode} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 14px", fontWeight: 600, fontSize: 17, cursor: "pointer" }}>{darkMode ? "☀️ Light" : "🌙 Dark"}</button>
        <button onClick={handleLogout} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 14px", fontWeight: 600, fontSize: 17, cursor: "pointer" }}>🚪 Logout</button>
      </DashboardHeader>

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", top: 80, right: 24, zIndex: 999, background: toast.startsWith("✅") ? "#f0fdf4" : "#fef2f2", border: `1px solid ${toast.startsWith("✅") ? "#86efac" : "#fecaca"}`, color: toast.startsWith("✅") ? "#16a34a" : "#dc2626", borderRadius: 12, padding: "14px 20px", fontWeight: 700, fontSize: 18, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
          {toast}
        </div>
      )}

      <div style={{ padding: "28px 32px" }}>

        {/* Stats Row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 24 }}>
          {[
            { label: "Faculty (Doctors)", value: realFacultyCount, color: "#2563eb", bg: "#eaf1ff", icon: "🎓" },
            { label: "Super Admins", value: userList.filter(u => u.role === "superadmin").length, color: "#d97706", bg: "#fef3c7", icon: "⭐" },
            { label: "Admins", value: userList.filter(u => u.role === "admin").length, color: "#7c3aed", bg: "#f5f3ff", icon: "👑" },
            { label: "Dept Accounts", value: deptAccountCount, color: "#64748b", bg: "#f1f5f9", icon: "🏢" },
          ].map((s, i) => (
            <div key={i} style={{ background: DM.cardBg(d), borderRadius: 14, padding: "18px 20px", boxShadow: DM.shadow(d), borderLeft: `4px solid ${s.color}` }}>
              <div style={{ fontSize: 32 }}>{s.icon}</div>
              <div style={{ fontSize: 32, fontWeight: 900, color: s.color, marginTop: 4 }}>{s.value}</div>
              <div style={{ fontSize: 16, color: DM.textSecondary(d), fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters and Actions */}
        <div style={{ background: DM.cardBg(d), borderRadius: 14, padding: "16px 20px", boxShadow: DM.shadow(d), marginBottom: 20, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: DM.inputBg(d), border: `1.5px solid ${DM.inputBorder(d)}`, borderRadius: 10, padding: "8px 14px", flex: 1, minWidth: 200 }}>
              <span>🔍</span>
              <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search name, email, emp ID, department..."
                style={{ background: "transparent", border: "none", outline: "none", fontSize: 17, color: DM.textPrimary(d), width: "100%" }} />
            </div>
            <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
              style={{ background: DM.inputBg(d), border: `1.5px solid ${DM.inputBorder(d)}`, borderRadius: 10, padding: "8px 14px", fontSize: 17, outline: "none", cursor: "pointer", color: DM.textPrimary(d) }}>
              <option value="all">All Roles</option>
              <option value="superadmin">⭐ Super Admin</option>
              <option value="admin">👑 Admin</option>
              <option value="faculty">🎓 Faculty</option>
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              style={{ background: DM.inputBg(d), border: `1.5px solid ${DM.inputBorder(d)}`, borderRadius: 10, padding: "8px 14px", fontSize: 17, outline: "none", cursor: "pointer", color: DM.textPrimary(d) }}>
              <option value="all">All Status</option>
              <option value="active">✅ Active</option>
              <option value="inactive">⛔ Inactive</option>
            </select>
            <button
              onClick={() => setShowDeptAccounts(p => !p)}
              title={showDeptAccounts ? "Hide department common accounts" : "Show department common accounts"}
              style={{ background: showDeptAccounts ? "#f59e0b" : DM.inputBg(d), border: `1.5px solid ${showDeptAccounts ? "#f59e0b" : DM.inputBorder(d)}`, borderRadius: 10, padding: "8px 14px", fontSize: 15, fontWeight: 700, cursor: "pointer", color: showDeptAccounts ? "#fff" : DM.textSecondary(d), whiteSpace: "nowrap" }}>
              🏢 {showDeptAccounts ? `Hide Dept (${deptAccountCount})` : `Show Dept (${deptAccountCount})`}
            </button>
            <span style={{ fontSize: 16, color: DM.textMuted(d), fontWeight: 600 }}>{filtered.length} users</span>
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            {selectedUsers.length > 0 && (
              <button onClick={openCredentialsModal}
                style={{ background: "#f59e0b", color: "#fff", border: "none", borderRadius: 12, padding: "10px 20px", fontWeight: 800, fontSize: 17, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "transform 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                <span>✉️</span> Send Credentials ({selectedUsers.length})
              </button>
            )}
            {adminProfile?.role === "superadmin" && (
              <button onClick={async () => {
                const facultyUsers = userList.filter(u => u.role === "faculty" || u.role === "admin");
                if (!window.confirm(`🔐 This will force ALL ${facultyUsers.length} faculty/admin users to reset their password on next login. Continue?`)) return;
                try {
                  let count = 0;
                  for (const u of facultyUsers) {
                    if (u.role !== "superadmin") {
                      await api.put(`/users/${u.id}`, { mustResetPassword: true });
                      count++;
                    }
                  }
                  showToast(`✅ Password reset enforced for ${count} users!`);
                  fetchAll();
                } catch (err) {
                  console.error(err);
                  showToast("❌ Failed to update users");
                }
              }}
                style={{ background: "#fef3c7", color: "#b45309", border: "1.5px solid #fde68a", borderRadius: 12, padding: "10px 20px", fontWeight: 800, fontSize: 17, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "transform 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                <span>🔐</span> Force Password Reset All
              </button>
            )}
            {adminProfile?.role === "superadmin" && (
              <button onClick={() => setShowImportModal(true)}
                style={{ background: "#f8fafc", color: "#1d4ed8", border: `1.5px solid #bfdbfe`, borderRadius: 12, padding: "10px 20px", fontWeight: 800, fontSize: 17, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "transform 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                <span>📥</span> Bulk Import
              </button>
            )}
            <button onClick={() => navigate("/admin/create-faculty")} 
              style={{ background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff", border: "none", borderRadius: 12, padding: "10px 20px", fontWeight: 800, fontSize: 17, cursor: "pointer", boxShadow: "0 4px 12px rgba(16,185,129,0.3)", display: "flex", alignItems: "center", gap: 8, transition: "transform 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
              onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
              <span style={{ fontSize: 20 }}>+</span> Create Faculty
            </button>
          </div>
        </div>

        {/* Users Table */}
        <div style={{ background: DM.cardBg(d), borderRadius: 16, boxShadow: DM.shadow(d), overflow: "visible" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 17 }}>
            <thead>
              <tr style={{ background: d ? "#0f172a" : "#f8fafc" }}>
                <th style={{ padding: "12px 16px", width: 40, textAlign: "center", borderBottom: `1px solid ${DM.border(d)}`, borderTopLeftRadius: 16 }}>
                  <input type="checkbox" checked={selectedUsers.length === filtered.length && filtered.length > 0} onChange={handleSelectAll} style={{ width: 18, height: 18, cursor: "pointer", accentColor: "#2563eb" }} />
                </th>
                {["User", "Emp ID", "Department", "Role", "Password", "Last Login", "Actions"].map((h, i) => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: h === "Password" ? "center" : "left", fontSize: 15, fontWeight: 700, color: d ? "#94a3b8" : "#64748b", letterSpacing: 0.5, borderBottom: `1px solid ${DM.border(d)}`, borderTopRightRadius: i === 6 ? 16 : 0 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>⏳ Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>No users found</td></tr>
              ) : filtered.map((u, i) => (
                <tr key={u.id} style={{ borderBottom: `1px solid ${DM.border(d)}`, opacity: u.status === "inactive" ? 0.6 : 1 }}
                  onMouseEnter={e => e.currentTarget.style.background = d ? "#1e293b" : "#f8fafc"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>

                  {/* Checkbox */}
                  <td style={{ padding: "13px 16px", textAlign: "center" }}>
                     <input type="checkbox" checked={selectedUsers.includes(u.id)} onChange={() => handleSelectUser(u.id)} style={{ width: 18, height: 18, cursor: "pointer", accentColor: "#2563eb" }} />
                  </td>

                  {/* User */}
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: u.role === "superadmin" ? "linear-gradient(135deg,#d97706,#b45309)" : u.role === "admin" ? "linear-gradient(135deg,#7c3aed,#6d28d9)" : `hsl(${i*47},65%,55%)`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 18, flexShrink: 0 }}>
                        {u.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, color: DM.textPrimary(d), fontSize: 17, display: "flex", alignItems: "center", gap: 6 }}>
                          {getNameWithTitle(u)}
                          {u.id === currentUser?.uid && <span style={{ background: "#eaf1ff", color: "#2563eb", borderRadius: 20, padding: "1px 7px", fontSize: 15, fontWeight: 700 }}>You</span>}
                          {u.status === "inactive" && <span style={{ background: "#fef2f2", color: "#dc2626", borderRadius: 20, padding: "1px 7px", fontSize: 15, fontWeight: 700 }}>⛔ Inactive</span>}
                        </div>
                        <div style={{ fontSize: 15, color: DM.textMuted(d) }}>{u.email}</div>
                      </div>
                    </div>
                  </td>

                  <td style={{ padding: "13px 16px", color: DM.textSecondary(d) }}>{u.empId || "—"}</td>
                  <td style={{ padding: "13px 16px", color: DM.textSecondary(d), maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{getDept(u) || "—"}</td>

                  {/* Role */}
                  <td style={{ padding: "13px 16px" }}>
                    <span style={{ background: roleBg[u.role] || "#f1f5f9", color: roleColor[u.role] || "#64748b", borderRadius: 20, padding: "3px 10px", fontSize: 16, fontWeight: 700 }}>
                      {roleLabel[u.role] || u.role}
                    </span>
                  </td>

                  {/* Password */}
                  <td style={{ padding: "13px 16px", textAlign: "center" }}>
                    <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, background: d ? "rgba(255,255,255,0.05)" : "#f8fafc", borderRadius: 8, padding: "4px 8px", border: `1px solid ${DM.border(d)}`, minWidth: 120 }}>
                      <span style={{ fontSize: 16, fontWeight: 700, color: DM.textSecondary(d), fontFamily: "monospace", letterSpacing: showPass[u.id] ? 0 : 2 }}>
                        {showPass[u.id] ? (u.password || "—") : "••••••••"}
                      </span>
                      {u.password && (
                        <button onClick={(e) => { e.stopPropagation(); togglePass(u.id); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, padding: 0 }}>
                          {showPass[u.id] ? "🙈" : "👁️"}
                        </button>
                      )}
                    </div>
                  </td>

                  {/* Last Login */}
                  <td style={{ padding: "13px 16px", color: DM.textSecondary(d) }}>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>
                      {u.lastLogin ? new Date(u.lastLogin).toLocaleString() : "Never"}
                    </div>
                  </td>

                  {/* Actions Dropdown */}
                  <td style={{ padding: "13px 16px", position: "relative" }}>
                    <button onClick={() => setOpenMenuId(openMenuId === u.id ? null : u.id)}
                      style={{ background: "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontWeight: 700, cursor: "pointer", fontSize: 16 }}>
                      ⋮ Actions
                    </button>
                    {openMenuId === u.id && (
                      <div style={{ position: "absolute", top: "100%", right: 16, marginTop: 4, background: "#fff", borderRadius: 12, boxShadow: "0 8px 32px rgba(15,37,87,0.18)", zIndex: 9999, minWidth: 190, overflow: "hidden", border: "1px solid #e2e8f0" }}
                        onMouseLeave={() => setOpenMenuId(null)}>

                        {u.role !== "superadmin" && (
                          <div onClick={() => { navigate(`/admin/edit-faculty/${u.id}`); setOpenMenuId(null); }}
                            style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#fff7ed"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            <span>✏️</span><span style={{ fontSize: 17, fontWeight: 600, color: "#ea580c" }}>Edit Details</span>
                          </div>
                        )}

                        {u.role !== "superadmin" && (
                          <div onClick={() => { navigate(`/admin/faculty/${u.id}`); setOpenMenuId(null); }}
                            style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#eaf1ff"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            <span>👁️</span><span style={{ fontSize: 17, fontWeight: 600, color: "#2563eb" }}>View Profile</span>
                          </div>
                        )}

                        {adminProfile?.role === "superadmin" && u.role !== "superadmin" && (
                          <div onClick={() => { setSelectedRightsUser(u); setOpenMenuId(null); }}
                            style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            <span>🔐</span><span style={{ fontSize: 17, fontWeight: 600, color: "#4338ca" }}>Manage Rights</span>
                          </div>
                        )}

                        <div onClick={() => { handlePasswordReset(u.email); setOpenMenuId(null); }}
                          style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#fffbeb"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <span>🔑</span><span style={{ fontSize: 17, fontWeight: 600, color: "#d97706" }}>Reset Password</span>
                        </div>

                        {u.id !== currentUser?.uid && u.role !== "superadmin" && (
                          <div onClick={() => { handleToggleStatus(u); setOpenMenuId(null); }}
                            style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
                            onMouseEnter={e => e.currentTarget.style.background = u.status === "inactive" ? "#f0fdf4" : "#fef2f2"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            <span>{u.status === "inactive" ? "✅" : "⛔"}</span>
                            <span style={{ fontSize: 17, fontWeight: 600, color: u.status === "inactive" ? "#16a34a" : "#dc2626" }}>
                              {u.status === "inactive" ? "Activate User" : "Deactivate User"}
                            </span>
                          </div>
                        )}

                        {u.role === "faculty" && (
                          <div onClick={() => { handleMakeAdmin(u); setOpenMenuId(null); }}
                            style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", cursor: "pointer" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#f0fdf4"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            <span>👑</span><span style={{ fontSize: 17, fontWeight: 600, color: "#16a34a" }}>Make Admin</span>
                          </div>
                        )}

                        {u.role === "admin" && u.id !== currentUser?.uid && (
                          <div onClick={() => { handleRemoveAdmin(u); setOpenMenuId(null); }}
                            style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#fef2f2"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            <span>❌</span><span style={{ fontSize: 17, fontWeight: 600, color: "#dc2626" }}>Remove Admin</span>
                          </div>
                        )}

                        {u.role !== "superadmin" && u.id !== currentUser?.uid && (
                          <div onClick={() => { handleDeleteUser(u); setOpenMenuId(null); }}
                            style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", cursor: "pointer" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#fee2e2"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            <span>🗑️</span><span style={{ fontSize: 17, fontWeight: 600, color: "#ef4444" }}>Delete User</span>
                          </div>
                        )}
                      </div>

                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>



      {selectedRightsUser && (
        <UserRightsModal 
          user={selectedRightsUser} 
          onClose={() => setSelectedRightsUser(null)} 
          onSuccess={() => {
            setSelectedRightsUser(null);
            showToast(`✅ Rights updated for ${selectedRightsUser.name}`);
            fetchAll();
          }}
        />
      )}

      {showImportModal && (
        <BulkImportModal
          onClose={() => { setShowImportModal(false); fetchAll(); }}
          onSuccess={() => fetchAll()}
          createBulkFacultyAccounts={createBulkFacultyAccounts}
        />
      )}

      {credentialsModalUsers && (
         <SendCredentialsModal 
           users={credentialsModalUsers}
           onClose={() => { setCredentialsModalUsers(null); setSelectedUsers([]); }}
           handlePasswordReset={handlePasswordReset}
         />
      )}
    </div>
  );
}

function SendCredentialsModal({ users, onClose, handlePasswordReset }) {
  const [sendingStatuses, setSendingStatuses] = useState({});

  const handleSendSingle = async (u, type) => {
    const waText = encodeURIComponent(`Dear ${u.name},\n\nYour APDCH Faculty Login Credentials:\n*Username:* ${u.email}\n*Password:* ${u.password || 'Welcome@123'}\n\nPlease login securely.`);
    
    if (type === 'wa') {
      if (!u.mobile) return alert("No mobile number found for this user.");
      const waNum = u.mobile.replace(/\D/g, '');
      const waLink = `https://wa.me/${waNum.length === 10 ? '91' + waNum : waNum}?text=${waText}`;
      window.open(waLink, '_blank');
    } else {
      setSendingStatuses(prev => ({ ...prev, [u.id]: 'sending' }));
      try {
        await handlePasswordReset(u.email);
        setSendingStatuses(prev => ({ ...prev, [u.id]: 'done' }));
      } catch (err) {
        setSendingStatuses(prev => ({ ...prev, [u.id]: 'error' }));
      }
    }
  };

  const handleSendAllEmails = async () => {
    if (!window.confirm(`Automatically send login link emails to ${users.length} user(s)?`)) return;
    for (const u of users) {
      setSendingStatuses(prev => ({ ...prev, [u.id]: 'sending' }));
      try {
        await handlePasswordReset(u.email);
        setSendingStatuses(prev => ({ ...prev, [u.id]: 'done' }));
      } catch (err) {
        setSendingStatuses(prev => ({ ...prev, [u.id]: 'error' }));
      }
      await new Promise(r => setTimeout(r, 400));
    }
  };

  const handleSendAllWa = async () => {
    if (!window.confirm(`This will open WhatsApp tabs for ${users.length} user(s). Ensure popups are allowed! Continue?`)) return;
    let delay = 0;
    users.forEach((u) => {
      setTimeout(() => {
        if (u.mobile) {
            const waText = encodeURIComponent(`Dear ${u.name},\n\nYour APDCH Faculty Login Credentials:\n*Username:* ${u.email}\n*Password:* ${u.password || 'Welcome@123'}\n\nPlease login securely.`);
            const waNum = u.mobile.replace(/\D/g, '');
            const waLink = `https://wa.me/${waNum.length === 10 ? '91' + waNum : waNum}?text=${waText}`;
            window.open(waLink, '_blank');
        }
      }, delay);
      delay += 800; 
    });
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }}>
      <div style={{ background: "#fff", borderRadius: 24, padding: "32px", width: "100%", maxWidth: 650, boxShadow: "0 24px 48px rgba(0,0,0,0.2)", fontFamily: "'Nunito', sans-serif" }}>
        
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: "0 0 4px 0", fontSize: 24, color: "#0f2557", fontWeight: 900 }}>✉️ Send Credentials</h2>
            <p style={{ margin: 0, color: "#64748b", fontSize: 17, fontWeight: 600 }}>Send login details directly to selected users.</p>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "#f1f5f9", borderRadius: 10, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#64748b", fontWeight: 900 }}>✕</button>
        </div>

        <div style={{ maxHeight: "50vh", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 16, marginBottom: 20 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 16 }}>
             <thead style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 10 }}>
               <tr>
                 <th style={{ padding: "12px 16px", textAlign: "left", color: "#475569", borderBottom: "1px solid #e2e8f0" }}>User</th>
                 <th style={{ padding: "12px 16px", textAlign: "center", color: "#475569", borderBottom: "1px solid #e2e8f0" }}>Action</th>
               </tr>
             </thead>
             <tbody>
               {users.map(u => (
                 <tr key={u.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "12px 16px" }}>
                       <div style={{ fontWeight: 700, color: "#0f172a" }}>{u.name}</div>
                       <div style={{ fontSize: 14, color: "#64748b" }}>{u.email} • {u.mobile || 'No Mobile'}</div>
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "center", display: "flex", justifyContent: "center", gap: 8 }}>
                       <button onClick={() => handleSendSingle(u, 'wa')} disabled={!u.mobile} style={{ background: u.mobile ? "#dfebd5" : "#f1f5f9", color: u.mobile ? "#16a34a" : "#cbd5e1", border: "none", borderRadius: 8, padding: "6px 12px", fontWeight: 700, cursor: u.mobile ? "pointer" : "not-allowed" }}>WhatsApp</button>
                       <button onClick={() => handleSendSingle(u, 'email')} disabled={sendingStatuses[u.id] === 'done'} style={{ background: sendingStatuses[u.id] === 'done' ? "#f0fdf4" : "#e0e7ff", color: sendingStatuses[u.id] === 'done' ? "#16a34a" : "#4338ca", border: "none", borderRadius: 8, padding: "6px 12px", fontWeight: 700, cursor: sendingStatuses[u.id] === 'done' ? "default" : "pointer", width: 90 }}>
                          {sendingStatuses[u.id] === 'sending' ? '⏳...' : sendingStatuses[u.id] === 'done' ? '✅ Sent' : 'Auto Email'}
                       </button>
                    </td>
                 </tr>
               ))}
             </tbody>
          </table>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "14px", background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 12, fontWeight: 800, fontSize: 17, cursor: "pointer" }}>Close</button>
          
          <button onClick={handleSendAllEmails} style={{ flex: 1.5, padding: "14px", background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 12, fontWeight: 800, fontSize: 17, cursor: "pointer", display: "flex", justifyContent: "center", gap: 8 }}>
            <span>✉️</span> Auto Email All
          </button>
          
          <button onClick={handleSendAllWa} style={{ flex: 1.5, padding: "14px", background: "#10b981", color: "#fff", border: "none", borderRadius: 12, fontWeight: 800, fontSize: 17, cursor: "pointer", display: "flex", justifyContent: "center", gap: 8 }}>
            <span>💬</span> WhatsApp All
          </button>
        </div>
      </div>
    </div>
  );
}

function UserRightsModal({ user, onClose, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [rights, setRights] = useState(user.rights || []);

  const toggleRight = (key) => setRights(prev => prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]);

  async function handleSave() {
    setSaving(true);
    try {
      await api.put(`/users/${user.id}`, { rights });
      onSuccess();
    } catch (err) { 
      console.error(err);
      alert("Failed to save rights");
    }
    setSaving(false);
  }

  const rightsOptions = [
    { key: "all_faculty_report", label: "📊 All Faculty Reports" },
    { key: "staff_details_report", label: "👥 Staff Details Report" },
    { key: "faculty_list", label: "📋 Faculty List" },
    { key: "staff_affidavit_dci", label: "📄 Staff Affidavit DCI" },    
    { key: "metrics_report", label: "📈 Metrics Report" },
    { key: "star_performer", label: "⭐ Star Performer" },

    { key: "charts", label: "📈 Dashboard Charts" },
    { key: "create_faculty", label: "➕ Create Faculty" },
    { key: "user_management", label: "👥 User Management" },
    { key: "designation", label: "🎖️ Designation" },
    { key: "section_fields", label: "📋 Section Fields" },
    { key: "sections", label: "⚙️ Manage Sections" },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }}>
      <div style={{ background: "#fff", borderRadius: 24, padding: "32px", width: "100%", maxWidth: 450, boxShadow: "0 24px 48px rgba(0,0,0,0.2)", fontFamily: "'Nunito', sans-serif" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: "0 0 4px 0", fontSize: 24, color: "#0f2557", fontWeight: 900 }}>🔐 Manage Rights</h2>
            <p style={{ margin: 0, color: "#64748b", fontSize: 17, fontWeight: 600 }}>{user.name} ({user.role === "admin" ? "Admin" : "Faculty"})</p>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "#f1f5f9", borderRadius: 10, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#64748b", fontWeight: 900 }}>✕</button>
        </div>
        
        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: "60vh", overflowY: "auto", padding: "4px 0" }}>
          {rightsOptions.map(opt => (
            <label key={opt.key} style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer", background: rights.includes(opt.key) ? "#eff6ff" : "#f8fafc", border: `1.5px solid ${rights.includes(opt.key) ? "#93c5fd" : "#e2e8f0"}`, padding: "14px 16px", borderRadius: 12, transition: "all 0.2s" }}>
              <input type="checkbox" checked={rights.includes(opt.key)} onChange={() => toggleRight(opt.key)} style={{ width: 18, height: 18, accentColor: "#2563eb", cursor: "pointer" }} />
              <span style={{ fontSize: 18, fontWeight: 700, color: rights.includes(opt.key) ? "#1d4ed8" : "#475569" }}>{opt.label}</span>
            </label>
          ))}
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 28 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "12px", background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 12, fontWeight: 800, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: "12px", background: "linear-gradient(135deg, #1d4ed8, #2563eb)", color: "#fff", border: "none", borderRadius: 12, fontWeight: 800, cursor: saving ? "not-allowed" : "pointer" }}>
            {saving ? "⏳ Saving..." : "✅ Save Rights"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkImportModal({ onClose, onSuccess, createBulkFacultyAccounts }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [progress, setProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);

  async function downloadTemplate() {
    try {
      const XLSX = await import("xlsx");
      const headers = ["Staff Name", "Email", "Password", "Employee ID", "Mobile", "Department", "Designation", "Role"];
      const ffRes = await api.get("/faculty_profile_fields?orderBy=order");
      const excludeVariants = [
        "name", "full name", "staff name", "faculty name",
        "email", "email id", "e-mail", "email address", "mail",
        "password", "pwd",
        "emp id", "employee id", "staff id", "empid",
        "mobile", "contact number", "phone number", "mobile number", "phone",
        "department", "dept",
        "designation",
        "role"
      ];
      const fields = mapRows(ffRes).map((d) => d.name).filter((f) =>
        !headers.some(h => h.toLowerCase() === f.toLowerCase()) &&
        !excludeVariants.includes(f.toLowerCase().trim())
      );
      
      const allHeaders = [...headers, ...fields];
      // Add sample row to show expected format
      const sampleRow = ["Dr. John Doe", "drjohn@example.com", "Welcome@123", "EMP001", "9876543210", "Oral Surgery", "Professor", "faculty"];
      // Pad sample row for extra fields
      while (sampleRow.length < allHeaders.length) sampleRow.push("");

      const ws = XLSX.utils.aoa_to_sheet([allHeaders, sampleRow]);
      // Auto-size columns
      ws["!cols"] = allHeaders.map(h => ({ wch: Math.max(h.length + 4, 18) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Faculty_Template");
      XLSX.writeFile(wb, "faculty_import_template.xlsx");
    } catch (err) { alert("Failed to generate template: " + err.message); }
  }

  function handleFileSelect(e) {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setStatus("");
    }
  }

  async function handleUpload() {
    if (!selectedFile) return;

    setLoading(true);
    setStatus("Reading file...");
    setProgress(0);

    try {
      const XLSX = await import("xlsx");
      const buffer = await selectedFile.arrayBuffer();
      const wb = XLSX.read(buffer, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rawData = XLSX.utils.sheet_to_json(ws, { blankrows: false });
      
      // Normalize all row keys — trim whitespace and handle invisible characters
      const data = rawData.map(row => {
        const clean = {};
        Object.keys(row).forEach(key => {
          const cleanKey = key.toString().replace(/[\u200B\u200C\u200D\uFEFF\u00A0]/g, "").trim();
          clean[cleanKey] = row[key];
        });
        return clean;
      }).filter(row => Object.values(row).some(v => v !== undefined && v !== null && v.toString().trim() !== ""));

      if (data.length === 0) {
        setStatus("❌ File is empty — no data rows found.");
        setLoading(false);
        return;
      }

      // Log headers for debugging
      const detectedHeaders = Object.keys(data[0]);
      console.log("Excel headers detected:", detectedHeaders);
      console.log("First row data:", JSON.stringify(data[0]));

      // Flexible value getter — case-insensitive with partial matching
      const getVal = (row, ...aliases) => {
        const keys = Object.keys(row);
        // Exact match first (case-insensitive)
        for (const alias of aliases) {
          const exactKey = keys.find(k => k.toLowerCase().trim() === alias.toLowerCase());
          if (exactKey !== undefined && row[exactKey] !== undefined && row[exactKey] !== null) {
            const val = row[exactKey].toString().trim();
            if (val) return val;
          }
        }
        // Partial/contains match as fallback
        for (const alias of aliases) {
          const partialKey = keys.find(k => k.toLowerCase().includes(alias.toLowerCase()) || alias.toLowerCase().includes(k.toLowerCase()));
          if (partialKey !== undefined && row[partialKey] !== undefined && row[partialKey] !== null) {
            const val = row[partialKey].toString().trim();
            if (val) return val;
          }
        }
        return "";
      };

      setStatus(`Parsed ${data.length} rows. Preparing accounts...`);

      // Auto-detect email column if standard names don't match
      const emailAliases = ["Email", "Email ID", "E-mail", "Email Address", "Mail"];
      let emailColName = "";
      const firstRowKeys = Object.keys(data[0]);
      // Try standard name matching first
      for (const alias of emailAliases) {
        const found = firstRowKeys.find(k => k.toLowerCase().trim() === alias.toLowerCase());
        if (found) { emailColName = found; break; }
      }
      // Partial match
      if (!emailColName) {
        const found = firstRowKeys.find(k => k.toLowerCase().includes("email") || k.toLowerCase().includes("mail"));
        if (found) emailColName = found;
      }
      // Auto-detect: find column whose values contain "@"
      if (!emailColName) {
        for (const key of firstRowKeys) {
          const val = data[0][key]?.toString() || "";
          if (val.includes("@")) { emailColName = key; break; }
        }
      }
      console.log("Email column detected:", emailColName || "NONE");

      const ffRes = await api.get("/faculty_profile_fields?orderBy=order");
      const fieldsDef = mapRows(ffRes);

      const rows = [];
      for (const row of data) {
        const name = getVal(row, "Staff Name", "Name", "Full Name", "Faculty Name", "Staff");
        // Use detected email column, or fallback to standard aliases
        const email = emailColName ? (row[emailColName]?.toString().trim() || "") : getVal(row, ...emailAliases);
        const password = getVal(row, "Password") || "Welcome@123";
        const rawRole = getVal(row, "Role").toLowerCase();
        const role = (rawRole === "admin" || rawRole === "superadmin") ? rawRole : "faculty";

        if (!email || !name) continue;

        const defaultSections = {
          articlePublications: [], textBookPublications: [], patents: [], copyrights: [],
          fellowships: [], phd: [], swayamCourses: [], awards: [], innovativeTeaching: [],
          eContent: [], researchGrants: [], guestLectures: [], conferencePresentation: [], shortResearchProjects: [],
          educationalQualifications: [{ course: "", degree: "", major: "", allied: "", stream: "", yop: "", percentage: "", institution: "", board: "", attempts: 1, stateCouncil: "", regNo: "", regDate: "" }],
          workExperiences: [{ name: "", jobTitle: "", fromDate: "", toDate: "", description: "", totalExp: "", attachments: "" }],
          designationHistory: [{ dept: "", designation: "", unit: "", fromDate: "", toDate: "", isMain: true }]
        };

        const profileData = { ...defaultSections, role, name, email, department: getVal(row, "Department", "Dept"), designation: getVal(row, "Designation"), empId: getVal(row, "Employee ID", "Emp ID", "Staff ID"), mobile: getVal(row, "Mobile", "Phone", "Contact Number", "Mobile Number") };

        fieldsDef.forEach(f => {
          const k = f.key || f.name.toLowerCase().replace(/[^a-z0-9]/g, "_");
          if (row[f.name] !== undefined) profileData[k] = row[f.name].toString();
        });

        rows.push({ email, password, profileData });
      }

      if (rows.length === 0) {
        const headerList = detectedHeaders.slice(0, 8).map(h => `"${h}"`).join(", ");
        const firstRowName = data[0]?.["Staff Name"] || data[0]?.["Name"] || "(not found)";
        const firstRowEmail = data[0]?.["Email"] || "(not found)";
        console.log("ALL ROW KEYS:", Object.keys(data[0]));
        console.log("ALL ROW VALUES:", Object.values(data[0]));
        setStatus(`❌ ${data.length} rows parsed but no valid data. Headers: ${headerList}. Row1 Name="${firstRowName}", Email="${firstRowEmail}". Check browser console (F12) for details.`);
        setLoading(false);
        return;
      }

      setStatus(`Creating ${rows.length} accounts...`);

      const result = await createBulkFacultyAccounts(rows, (current, total) => {
        setProgress(Math.round((current / total) * 100));
        setStatus(`Processing ${current} of ${total}...`);
      });

      const skipped = data.length - rows.length;
      setStatus(`✅ Done! Added: ${result.success}, Failed: ${result.fail}${skipped > 0 ? `, Skipped (no name/email): ${skipped}` : ""}`);
      setSelectedFile(null);
      onSuccess();
    } catch (e) {
      console.error("Bulk import error:", e);
      setStatus("❌ Error: " + e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }}>
      <div style={{ background: "#fff", borderRadius: 24, padding: "32px", width: "100%", maxWidth: 450, boxShadow: "0 24px 48px rgba(0,0,0,0.2)", fontFamily: "'Nunito', sans-serif" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: "0 0 4px 0", fontSize: 24, color: "#0f2557", fontWeight: 900 }}>📥 Bulk Import Faculty</h2>
            <p style={{ margin: 0, color: "#64748b", fontSize: 17, fontWeight: 600 }}>Create multiple accounts from an Excel sheet.</p>
          </div>
          <button onClick={onClose} disabled={loading} style={{ border: "none", background: "#f1f5f9", borderRadius: 10, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: loading ? "not-allowed" : "pointer", color: "#64748b", fontWeight: 900 }}>✕</button>
        </div>
        
        <div style={{ marginBottom: 24, background: "#eaf1ff", padding: 16, borderRadius: 12 }}>
          <h4 style={{ margin: "0 0 8px 0", color: "#1d4ed8", fontSize: 17 }}>Step 1: Download Template</h4>
          <p style={{ margin: "0 0 12px 0", color: "#3b82f6", fontSize: 16 }}>Use our Excel template. <b>Name</b> and <b>Email</b> are mandatory. Missing passwords default to <code>Welcome@123</code>.</p>
          <button onClick={downloadTemplate} disabled={loading} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 700, fontSize: 16, cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 8 }}><span>📄</span> Download Template</button>
        </div>

        <div style={{ background: "#f8fafc", padding: 16, borderRadius: 12 }}>
          <h4 style={{ margin: "0 0 8px 0", color: "#475569", fontSize: 17 }}>Step 2: Upload Data</h4>
          <input type="file" accept=".xlsx, .xls" onChange={handleFileSelect} disabled={loading} style={{ width: "100%", padding: 10, border: "1.5px dashed #cbd5e1", borderRadius: 8, background: "#fff", cursor: loading ? "not-allowed" : "pointer", fontSize: 16, boxSizing: "border-box" }} />
          {selectedFile && (
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 15, color: "#475569", fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📎 {selectedFile.name}</span>
              <span style={{ fontSize: 14, color: "#94a3b8", fontWeight: 600, whiteSpace: "nowrap" }}>({(selectedFile.size / 1024).toFixed(1)} KB)</span>
            </div>
          )}
          <button onClick={handleUpload} disabled={loading || !selectedFile}
            style={{ marginTop: 14, width: "100%", padding: "12px 20px", background: (!selectedFile || loading) ? "#cbd5e1" : "linear-gradient(135deg, #10b981, #059669)", color: (!selectedFile || loading) ? "#94a3b8" : "#fff", border: "none", borderRadius: 10, fontWeight: 800, fontSize: 17, cursor: (!selectedFile || loading) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, boxShadow: (!selectedFile || loading) ? "none" : "0 4px 14px rgba(16,185,129,0.3)", transition: "all 0.2s" }}>
            {loading ? "⏳ Importing..." : "🚀 Upload & Import"}
          </button>
        </div>

        {status && (
          <div style={{ marginTop: 20, textAlign: "center" }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: status.includes("Done") ? "#16a34a" : status.includes("❌") ? "#dc2626" : "#ca8a04", marginBottom: 8 }}>{status}</div>
            {loading && (
              <div style={{ width: "100%", background: "#e2e8f0", borderRadius: 10, height: 8, overflow: "hidden" }}>
                <div style={{ height: "100%", background: "#2563eb", width: `${progress}%`, transition: "width 0.3s" }}></div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
